
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EnhancedDatePicker } from '@/components/ui/enhanced-date-picker';
import { Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

interface BookFormat {
  format: string;
  isbn: string;
  publishDate: string;
}

interface BookFormatFormProps {
  formats: BookFormat[];
  onFormatsChange: (formats: BookFormat[]) => void;
}

const BookFormatForm: React.FC<BookFormatFormProps> = ({ formats, onFormatsChange }) => {
  const addFormat = () => {
    const newFormat: BookFormat = {
      format: 'paperback',
      isbn: '',
      publishDate: format(new Date(), 'yyyy-MM-dd')
    };
    onFormatsChange([...formats, newFormat]);
  };

  const removeFormat = (index: number) => {
    const newFormats = formats.filter((_, i) => i !== index);
    onFormatsChange(newFormats);
  };

  const updateFormat = (index: number, field: keyof BookFormat, value: string) => {
    const newFormats = formats.map((format, i) => 
      i === index ? { ...format, [field]: value } : format
    );
    onFormatsChange(newFormats);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-base font-medium">Book Formats</Label>
        <Button type="button" variant="outline" size="sm" onClick={addFormat}>
          <Plus className="h-4 w-4 mr-1" />
          Add Format
        </Button>
      </div>
      
      {formats.map((bookFormat, index) => (
        <div key={index} className="border p-4 rounded-lg space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium">Format {index + 1}</h4>
            {formats.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFormat(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor={`format-${index}`}>Format</Label>
              <Select 
                value={bookFormat.format} 
                onValueChange={(value) => updateFormat(index, 'format', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paperback">Paperback</SelectItem>
                  <SelectItem value="hardcover">Hardcover</SelectItem>
                  <SelectItem value="ebook">E-book</SelectItem>
                  <SelectItem value="audiobook">Audiobook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor={`isbn-${index}`}>ISBN</Label>
              <Input
                id={`isbn-${index}`}
                value={bookFormat.isbn}
                onChange={(e) => updateFormat(index, 'isbn', e.target.value)}
                placeholder="Enter ISBN"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor={`date-${index}`}>Publication Date</Label>
              <EnhancedDatePicker
                value={bookFormat.publishDate ? new Date(bookFormat.publishDate) : undefined}
                onSelect={(date) =>
                  updateFormat(index, 'publishDate', date ? format(date, 'yyyy-MM-dd') : '')
                }
                placeholder="Pick publication date"
                maxDate={new Date()}
                minDate={new Date("1900-01-01")}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BookFormatForm;
